import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { sanitizeHTML } from "~/utils/sanitizedHTML";
import { splitFirstLine } from "~/utils/announcement-text";

type Announcement = { id: string; body: string };

const DISMISSED_KEY = "reconizer.dismissedAnnouncements";

function readDismissed(): string[] {
  try {
    const parsed = JSON.parse(window.localStorage.getItem(DISMISSED_KEY) ?? "[]");
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === "string") : [];
  } catch {
    return [];
  }
}

function writeDismissed(ids: string[]) {
  try {
    window.localStorage.setItem(DISMISSED_KEY, JSON.stringify(ids));
  } catch {
    // Private mode or blocked storage: the banner simply returns on the next page load.
  }
}

/** One message: its first line on a salmon strip; click to read it in full. */
export function AnnouncementMessage({ body, onDismiss }: { body: string; onDismiss?: () => void }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  // Saved sanitized; sanitized again here so a bad row can never inject script.
  const safeBody = useMemo(() => sanitizeHTML(body), [body]);
  const { firstLine, hasMore } = useMemo(() => splitFirstLine(new DOMParser().parseFromString(safeBody, "text/html")), [safeBody]);
  const toggleLabel = String(expanded ? t("admin.messages.collapse", "Show less") : t("admin.messages.expand", "Show full message"));

  return (
    <div role="status" className="flex items-start gap-3 bg-[#FA8072] px-4 py-2.5 text-sm text-[#2b1210]">
      <i className="icon-bell mt-0.5 text-lg leading-none" aria-hidden="true" />
      {expanded ? (
        <div className="min-w-0 flex-1 break-words" dangerouslySetInnerHTML={{ __html: safeBody }} />
      ) : hasMore ? (
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="min-w-0 flex-1 truncate text-left font-medium outline-none focus-visible:underline"
          aria-expanded={false}
          title={toggleLabel}
        >
          {firstLine}
        </button>
      ) : (
        <span className="min-w-0 flex-1 truncate font-medium">{firstLine}</span>
      )}
      {hasMore && (
        <button
          type="button"
          onClick={() => setExpanded((open) => !open)}
          className="shrink-0 outline-none hover:opacity-70 focus-visible:opacity-70"
          aria-expanded={expanded}
          aria-label={toggleLabel}
          title={toggleLabel}
        >
          <i className={`${expanded ? "icon-chevron-up" : "icon-chevron-down"} text-base`} aria-hidden="true" />
        </button>
      )}
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 outline-none hover:opacity-70 focus-visible:opacity-70"
          aria-label={String(t("admin.messages.dismiss", "Dismiss message"))}
          title={String(t("admin.messages.dismiss", "Dismiss message"))}
        >
          <i className="icon-cross text-base" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}

export function AnnouncementBanner({ announcements }: { announcements: Announcement[] }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState<string[] | null>(null);

  useEffect(() => {
    // Keep only ids that still exist, so the stored list cannot grow forever.
    const current = new Set(announcements.map((a) => a.id));
    const kept = readDismissed().filter((id) => current.has(id));
    writeDismissed(kept);
    setDismissed(kept);
  }, [announcements]);

  if (!dismissed) return null;
  const visible = announcements.filter((a) => !dismissed.includes(a.id));
  if (!visible.length) return null;

  const dismiss = (id: string) => {
    const next = [...dismissed, id];
    writeDismissed(next);
    setDismissed(next);
  };

  return (
    <div className="mb-3 grid gap-2" role="region" aria-label={String(t("admin.messages.page_title", "Messages"))}>
      {visible.map((a) => (
        <AnnouncementMessage key={a.id} body={a.body} onDismiss={() => dismiss(a.id)} />
      ))}
    </div>
  );
}

/** Live messages above the sign-in form: 600px, centered on the page past the narrower login column, not dismissible. */
export function LoginAnnouncements({ announcements }: { announcements: Announcement[] }) {
  const { t } = useTranslation();
  if (!announcements.length) return null;
  return (
    <div
      className="relative left-1/2 mt-8 grid w-[600px] max-w-[calc(100vw-2rem)] -translate-x-1/2 gap-2"
      role="region"
      aria-label={String(t("admin.messages.page_title", "Messages"))}
    >
      {announcements.map((a) => (
        <AnnouncementMessage key={a.id} body={a.body} />
      ))}
    </div>
  );
}
